import { API_CONFIG } from './config'
import {
  getAccessToken as defaultGetAccessToken,
  refreshAccessToken as defaultRefreshAccessToken,
} from './client'

/**
 * Authenticated Server-Sent-Events transport over `fetch`.
 *
 * WHY THIS EXISTS. The native `EventSource` cannot set request headers, so the
 * four hooks that used it (announcements, lms, two phishing) put the access
 * token in the URL query string: `?access_token=<jwt>&workspace_id=<id>`. nginx
 * logs the full request line of every SSE disconnect as an
 * `[error] upstream prematurely closed connection`, so on production alone 872
 * JWT occurrences accumulated across rotated nginx logs — a live credential
 * leak that no amount of log scrubbing fixes while the mechanism keeps running.
 *
 * `fetch` CAN set headers. Six other streams here already stream SSE over fetch
 * with an `Authorization` header (AI chat, AI Studio, content-studio, the
 * editor, the O365 wizard, and use-esa-monitor-stream). This is that proven
 * pattern, generalised — the frame parser, 401→refresh→retry, reconnect ladder
 * and heartbeat watchdog are lifted near-verbatim from use-esa-monitor-stream,
 * which has run it in production. The token travels in the header, so it never
 * reaches a URL and there is nothing for nginx to log.
 *
 * This is a plain transport, NOT a React hook: each calling hook keeps its own
 * registry / refcount / dedup / query-invalidation exactly as before and only
 * swaps its `new EventSource(url)` block for a call here. Minimal blast radius.
 *
 * The BE is unchanged and backward-compatible: SseQueryAuthMiddleware only
 * fills `Authorization` when it is ABSENT, so a header-bearing fetch client just
 * works, and an old browser tab still on the query-param bundle keeps working
 * until it reloads. New leaks stop immediately; old-tab leaks stop as tabs turn
 * over.
 */

export interface EventStreamHandlers {
  /**
   * One decoded SSE frame. `name` is the `event:` field (or `'message'` when the
   * frame had none); `data` is the joined `data:` lines, verbatim. Matches what
   * `es.addEventListener(name, e => e.data)` delivered, so callers port directly.
   */
  onEvent: (name: string, data: string) => void
  /** Optional connection-state callback, for hooks that surface a status pill. */
  onStatus?: (status: 'connecting' | 'live' | 'reconnecting' | 'closed') => void
  /** Override the token source (tests, non-default realms). */
  getAccessToken?: () => string | null
  /** Override the refresh path (tests). */
  refreshAccessToken?: () => Promise<string | null>
  /**
   * Extra request headers, sent on every (re)connect — e.g. `x-domain-id`, so a
   * stream is checked in the same domain as the read it stands in for. They
   * can add to the request but never replace the token, the workspace or the
   * stream content type.
   */
  headers?: Record<string, string>
  /**
   * OPT-IN. Told the HTTP status of a refused response the transport is about
   * to retry on its ladder (408, 429 and 5xx; a 401/403/404 already ends the
   * stream). Return `'stop'` to end the stream instead — status `closed`, no
   * reconnect — and the caller then owns when, and whether, to open another.
   *
   * It exists for a refusal the ladder makes worse: a server that answers 429
   * because this user has too many streams open is re-asked every few seconds,
   * then every 30 s per tab, and every attempt counts against that same limit.
   * Absent, or any other return value, keeps the ladder exactly as it was.
   */
  onRefused?: (status: number) => 'stop' | void
  /** Base reconnect delay; the ladder doubles it to a cap. Default 1000ms. */
  reconnectBaseMs?: number
  /**
   * Half-open-connection watchdog, OPT-IN. When set, if no bytes arrive within
   * this many ms the reader is cancelled and the ladder reconnects.
   *
   * Default OFF, and that default is load-bearing: the campaign/lms/announcement
   * SSE endpoints do NOT heartbeat (plain @Sse() over a Redis pub/sub channel,
   * no ping), so a watchdog on would force-reconnect every idle page on a timer
   * — a storm the native EventSource these hooks replaced never caused. Only
   * enable it for a stream the server is known to ping (e.g. ESA). Without it,
   * behaviour matches the old EventSource: reconnect only when fetch surfaces a
   * real end/error.
   */
  heartbeatTimeoutMs?: number
}

/** The key the app stores the active workspace id under (see the existing hooks). */
const WORKSPACE_KEY = 'hf-app:workspace-id'
const RECONNECT_CAP_MS = 30_000

/**
 * Open an authenticated SSE stream to `path` (relative to the API versioned
 * base). Returns a `close()` that stops the stream and cancels any pending
 * reconnect — call it from the caller's unsubscribe/unmount, exactly where
 * `es.close()` was called.
 */
export function openEventStream(
  path: string,
  handlers: EventStreamHandlers,
): () => void {
  if (typeof window === 'undefined') return () => {}

  const getToken = handlers.getAccessToken ?? defaultGetAccessToken
  const refresh = handlers.refreshAccessToken ?? defaultRefreshAccessToken
  const baseDelay = handlers.reconnectBaseMs ?? 1000
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`

  let closed = false
  let controller: AbortController | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let watchdog: ReturnType<typeof setInterval> | null = null
  let attempt = 0

  const status = (s: 'connecting' | 'live' | 'reconnecting' | 'closed') => {
    if (!closed || s === 'closed') handlers.onStatus?.(s)
  }

  // fetch(url, init), not fetch(new Request(...)): identical behaviour, and it
  // drops a dependency on the global Request constructor (absent under jsdom,
  // so a Request-based helper is untestable without a polyfill).
  const buildInit = (token: string): RequestInit => ({
    method: 'GET',
    headers: {
      // The caller's extras first, so nothing they pass can displace the three below.
      ...handlers.headers,
      Authorization: `Bearer ${token}`,
      'x-workspace-id': window.localStorage.getItem(WORKSPACE_KEY) ?? '',
      Accept: 'text/event-stream',
    },
    signal: controller!.signal,
    // Never surface browser HTTP cache on an event stream.
    cache: 'no-store',
  })

  const scheduleReconnect = () => {
    if (closed) return
    status('reconnecting')
    const delay = Math.min(baseDelay * 2 ** attempt, RECONNECT_CAP_MS)
    attempt += 1
    reconnectTimer = setTimeout(run, delay)
  }

  const run = async () => {
    if (closed) return
    const token = getToken()
    // No token → do not connect, and do not spin. A later caller re-invocation
    // (which happens on auth change via the hook's effect) will retry.
    if (!token) return

    controller = new AbortController()
    status('connecting')

    let lastByteAt = Date.now()
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null

    // Liveness watchdog — only when the caller opts in AND the server pings;
    // otherwise an idle-but-healthy stream would be torn down on a timer. See
    // heartbeatTimeoutMs.
    if (watchdog) clearInterval(watchdog)
    const hbTimeout = handlers.heartbeatTimeoutMs
    if (hbTimeout && hbTimeout > 0) {
      watchdog = setInterval(() => {
        if (Date.now() - lastByteAt > hbTimeout) {
          reader?.cancel().catch(() => {})
        }
      }, Math.min(10_000, hbTimeout))
    }

    try {
      let res = await fetch(url, buildInit(token))
      if (res.status === 401) {
        const fresh = await refresh()
        if (fresh && !closed) res = await fetch(url, buildInit(fresh))
      }
      // A client error ENDS the stream — it must not feed the reconnect ladder.
      // A 401 that survived the refresh (or a 403/404) threw into the generic
      // catch below, so a page left open after its session died re-fetched and
      // re-ran the refresh every 30s, per stream, forever. The EventSource this
      // replaced failed permanently on a non-200. 408/429 are transient, and
      // network errors / 5xx (nginx 502 mid-deploy) still reconnect.
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        stop()
        return
      }
      // A refusal the ladder would retry: the caller may know it must not be
      // (see `onRefused`). Only asked when the caller opted in.
      if (!res.ok && handlers.onRefused?.(res.status) === 'stop') {
        stop()
        return
      }
      if (!res.ok || !res.body) throw new Error(`SSE request failed: ${res.status}`)

      reader = res.body.getReader()
      status('live')
      // The backoff ladder resets in dispatchFrame, on the first REAL frame —
      // not here. Headers alone prove nothing: NestJS commits 200 SSE headers
      // before the handler's async assertOwned settles, so a deleted campaign
      // answered 200-then-end every time, and a reset here pinned the ladder
      // at its base delay — one authenticated request per second, forever.

      const decoder = new TextDecoder()
      let buffer = ''
      let eventName = ''
      let dataLines: string[] = []

      const dispatchFrame = () => {
        const name = eventName || 'message'
        const data = dataLines.join('\n')
        eventName = ''
        dataLines = []
        // Bare blank-line keep-alives never reach here — the caller only
        // dispatches when an event or data line accumulated.
        // A delivered frame is what proves the connection healthy.
        attempt = 0
        handlers.onEvent(name, data)
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        lastByteAt = Date.now()
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const raw of lines) {
          const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw
          if (line === '') {
            // Frame terminator — dispatch only if we accumulated something.
            if (eventName || dataLines.length) dispatchFrame()
            continue
          }
          if (line.startsWith(':')) continue // SSE comment / heartbeat
          if (line.startsWith('event:')) {
            eventName = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            // Per spec, strip exactly one leading space after the colon.
            dataLines.push(line.slice(5).replace(/^ /, ''))
          }
        }
      }
    } catch {
      // fall through to reconnect — aborts (from close()) are caught here too
    } finally {
      if (watchdog) {
        clearInterval(watchdog)
        watchdog = null
      }
      reader?.cancel().catch(() => {})
    }

    scheduleReconnect()
  }

  // Terminal: no reconnect is scheduled after this. Distinct from the returned
  // close() only in that nothing outside asked for it.
  function stop() {
    if (closed) return
    closed = true
    status('closed')
  }

  void run()

  return () => {
    if (closed) return
    closed = true
    status('closed')
    if (reconnectTimer) clearTimeout(reconnectTimer)
    if (watchdog) clearInterval(watchdog)
    controller?.abort()
  }
}
